import React, { useEffect, useState } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import { Card, CardBody, Table, Button, Input } from "reactstrap"
import {
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
  FaChevronRight,
  FaChevronLeft,
} from "react-icons/fa"
import StaffModal from "./StaffModal"
import Swal from "sweetalert2"
import moment from "moment"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import { usePermissionData } from "AppContext"

const StaffIndex = () => {
  document.title = "Staff | Milano Jewelry"

  const { checkAccess } = usePermissionData()

  const [loading, setLoading] = useState(false)

  const [staffData, setStaffData] = useState([])
  const [Roles, setRoles] = useState([])
  const [searchString, setSearchString] = useState("")
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState("add")
  const [formData, setFormData] = useState({})

  useEffect(() => {
    getStaffData()
    getRolesData()
  }, [])

  async function getRolesData() {
    const APIdata = await fetchData(setLoading, "roles")
    if (APIdata?.success) {
      setRoles(APIdata?.data)
    }
  }

  async function getStaffData() {
    const APIdata = await fetchData(setLoading, "staff")
    if (APIdata?.success) {
      setStaffData(APIdata?.data)
    }
  }

  /* ================= SEARCH ================= */

  const filteredData = staffData.filter(item =>
    [item.name, item.email, item.phone, item.designation]
      .join(" ")
      .toLowerCase()
      .includes(searchString.toLowerCase())
  )

  /* ================= PAGINATION ================= */

  const totalPages = Math.ceil(filteredData.length / pageSize)

  const paginatedData = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  )

  /* ================= HANDLERS ================= */

  const openAddModal = () => {
    setFormData({})
    setModalMode("add")
    setModalOpen(true)
  }

  const openEditModal = item => {
    // Strip the stored (already-encrypted) password so it's never
    // round-tripped back to the API unless the user types a new one.
    setFormData({ ...item, password: "" })
    setModalMode("edit")
    setModalOpen(true)
  }

  const openViewModal = item => {
    setFormData(item)
    setModalMode("view")
    setModalOpen(true)
  }

  const handleSubmit = async () => {
    if (formData?.id) {
      // Only send the password if the user actually entered a new one,
      // so the existing encrypted password is never overwritten/re-sent.
      const { password, ...rest } = formData
      const payload = password?.trim() ? formData : rest

      const APIdata = await FetchPutData(
        setLoading,
        "staff",
        formData?.id,
        payload
      )
      if (APIdata?.data?.success) {
        setModalOpen(false)
        Swal.fire({
          title: "Staff Updated Successfully",
          text: APIdata?.data?.message,
          icon: "success",
        })
        setTimeout(() => {
          getStaffData()
        }, 1500)
      } else {
        Swal.fire({
          title: "Error Adding Staff",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    } else {
      const APIdata = await FetchPostData(setLoading, "staff", formData)
      if (APIdata?.data?.success) {
        setModalOpen(false)
        Swal.fire({
          title: "Staff Added Successfully",
          text: APIdata?.data?.message,
          icon: "success",
        })
        setTimeout(() => {
          getStaffData()
        }, 1500)
      } else {
        Swal.fire({
          title: "Error Adding Staff",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    }
  }

  const handleDelete = async item => {
    const confirm = await Swal.fire({
      title: "Are you Sure?",
      text: `you want to Delete this ${item?.name} Staff`,
      icon: "warning",
      showCancelButton: true,
    })

    if (!confirm.isConfirmed) {
      return
    }

    const APIdata = await FetchDeleteData(setLoading, "staff", item?.id)
    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Staff Deleted Successfully",
        text: APIdata?.data?.message,
        icon: "success",
      })
      setTimeout(() => {
        getStaffData()
      }, 1500)
    } else {
      Swal.fire({
        title: "Error deleting Staff",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  return (
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb title="Dashboard" breadcrumbItem="Staff" />

      <Card>
        <CardBody>
          {/* ===== TOP BAR ===== */}
          <div className="d-flex justify-content-between mb-3">
            <div className="d-flex gap-2">
              <select
                className="form-select"
                style={{ width: "100px" }}
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value))
                  setCurrentPage(1)
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>

              <Input
                placeholder="Search..."
                value={searchString}
                onChange={e => {
                  setSearchString(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </div>

            {checkAccess(4, "add") && (
              <Button className="btn-blue" onClick={openAddModal}>
                <FaPlus className="me-1" />
                Add Staff
              </Button>
            )}
          </div>

          {/* ===== TABLE ===== */}
          <Table striped responsive>
            <thead className="table-blue">
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Designation</th>
                <th>Joining Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center">
                    No Records Found
                  </td>
                </tr>
              )}

              {paginatedData.map((item, index) => (
                <tr key={item.id}>
                  <td>{(currentPage - 1) * pageSize + index + 1}</td>
                  <td>{item.name}</td>
                  <td>{item.email}</td>
                  <td>{item.phone}</td>
                  <td>{item.designation}</td>
                  <td>{moment(item.joiningDate).format("MM-DD-YYYY")}</td>
                  <td className="d-flex gap-2">
                    <Button
                      color="secondary"
                      onClick={() => openViewModal(item)}
                    >
                      <FaEye />
                    </Button>

                    {checkAccess(4, "edit") && (
                      <Button
                        color="success"
                        onClick={() => openEditModal(item)}
                      >
                        <FaEdit />
                      </Button>
                    )}

                    {checkAccess(4, "delete") && (
                      <Button
                        color="danger"
                        onClick={() => handleDelete(item)}
                      >
                        <FaTrash />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* ===== PAGINATION ===== */}
          <div className="d-flex justify-content-between">
            <span className="align-self-center">
              Page {currentPage} of {totalPages || 1}
            </span>
            {/* ===== PAGINATION ===== */}
            <div className="d-flex justify-content-center align-items-center gap-2 ">
              {/* Previous */}
              <Button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
                className="btn-blue"
              >
                <FaChevronLeft />
              </Button>

              {/* Page Numbers (Show 5 at a time) */}
              {(() => {
                const pageNumbers = []
                const maxVisiblePages = 5

                let startPage = Math.max(
                  1,
                  currentPage - Math.floor(maxVisiblePages / 2)
                )

                let endPage = startPage + maxVisiblePages - 1

                if (endPage > totalPages) {
                  endPage = totalPages
                  startPage = Math.max(1, endPage - maxVisiblePages + 1)
                }

                for (let i = startPage; i <= endPage; i++) {
                  pageNumbers.push(
                    <Button
                      key={i}
                      color={currentPage === i ? "primary" : "light"}
                      onClick={() => setCurrentPage(i)}
                    >
                      {i}
                    </Button>
                  )
                }

                return pageNumbers
              })()}

              {/* Next */}
              <Button
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage(prev => prev + 1)}
                className="btn-blue"
              >
                <FaChevronRight />
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ===== MODAL ===== */}
      <StaffModal
        open={modalOpen}
        close={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        mode={modalMode}
        Roles={Roles}
      />
    </div>
  )
}

export default StaffIndex
